import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import { tpaColorName } from '../../../../../../utils/color';

export type TextStylesParams = {
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  pageDescriptionFont: StyleParamType.Font;
  pageDescriptionColor: StyleParamType.Color;
  menuTitleFont: StyleParamType.Font;
  menuTitleColor: StyleParamType.Color;
  menuDescriptionFont: StyleParamType.Font;
  menuDescriptionColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  sectionTitleColor: StyleParamType.Color;
  sectionDescriptionFont: StyleParamType.Font;
  sectionDescriptionColor: StyleParamType.Color;
  itemTitleFont: StyleParamType.Font;
  itemTitleColor: StyleParamType.Color;
  itemDescriptionFont: StyleParamType.Font;
  itemDescriptionColor: StyleParamType.Color;
  itemPriceFont: StyleParamType.Font;
  itemPriceColor: StyleParamType.Color;
  itemUnavailablePriceFont: StyleParamType.Font;
  itemUnavailablePriceColor: StyleParamType.Color;
};

export const textStylesParams = createStylesParams<TextStylesParams>({
  pageTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { htmlTag: 'h2', size: 48 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 48 },
      ai: { description: 'Font of the menu page title' },
    },
  },
  pageTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-heading-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-title', opacity: 1 },
      ai: { description: 'Text color of the menu page title' },
    },
  },
  pageDescriptionFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font of the menu page description text' },
    },
  },
  pageDescriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Text color of the menu page description text' },
    },
  },
  menuTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { htmlTag: 'h2', size: 32 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 32 },
      ai: { description: 'Font of the menu title' },
    },
  },
  menuTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Text color of the menu title' },
    },
  },
  menuDescriptionFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font of the menu description text' },
    },
  },
  menuDescriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Text color of the menu description text' },
    },
  },
  sectionTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { htmlTag: 'h2', size: 28 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 28 },
      ai: { description: 'Font of the menu section title' },
    },
  },
  sectionTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Text color of the menu section title' },
    },
  },
  sectionDescriptionFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font of the menu section description text' },
    },
  },
  sectionDescriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Text color of the menu section description text' },
    },
  },
  itemTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font of the menu item title' },
    },
  },
  itemTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Text color of the menu item title' },
    },
  },
  itemDescriptionFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: { description: 'Font of the menu item description text' },
    },
  },
  itemDescriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Text color of the menu item description text' },
    },
  },
  itemPriceFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font of the menu item price' },
    },
  },
  itemPriceColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Text color of the menu item price' },
    },
  },
  itemUnavailablePriceFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: {
        description:
          'Font of the price shown for an unavailable (out of stock) menu item',
      },
    },
  },
  itemUnavailablePriceColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-3', opacity: 1 },
      ai: {
        description:
          'Text color of the price shown for an unavailable (out of stock) menu item',
      },
    },
  },
});
